import React, { useState } from "react";
import { Button } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CompanyInfoSection from "./CompanyInfoSection";
import ContactInfoSection from "./ContactInfoSection";
import AddressSection from "./AddressSection";
import BillingAddressSection from "./BillingAddressSection";
import SocialMediaSection from "./SocialMediaSection";
import ContactPersonsSection from "./ContactPersonsSection";
import MembershipDetailsSection from "./MembershipDetailsSection";
import PaymentInfoSection from "./PaymentInfoSection";
import OtherInformationSection from "./OtherInformationSection";

// Form state type
type CreateMemberForm = {
  companyInfo: {
    membershipType: string;
    fullTimeEmployees: string;
    partTimeEmployees: string;
    companyName: string;
    companyDescription: string;
    companyWebsite: string;
    companyPhone: string;
    companyEmail: string;
    companyCategory: string;
    inBusinessSince: string;
  };
  contactInfo: {
    firstName: string;
    lastName: string;
    title: string;
    phone: string;
    mobilePhone: string;
    email: string;
  };
  address: {
    streetAddress: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
  };
  billingAddress: {
    streetAddress: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
  };
  socialMedia: {
    facebook: string;
    instagram: string;
    linkedin: string;
    tiktok: string;
    twitter: string;
    threads: string;
    youtube: string;
  };
  contactPersons: {
    primary: {
      firstName: string;
      lastName: string;
      title: string;
      companyPhone: string;
      mobilePhone: string;
      email: string;
    };
    billing: {
      firstName: string;
      lastName: string;
      title: string;
      companyPhone: string;
      mobilePhone: string;
      email: string;
    };
    perks: {
      firstName: string;
      lastName: string;
      title: string;
      companyPhone: string;
      mobilePhone: string;
      email: string;
    };
    hr: {
      firstName: string;
      lastName: string;
      title: string;
      companyPhone: string;
      mobilePhone: string;
      email: string;
    };
  };
  membershipDetails: {
    hearAboutSource: string;
    reasonsForJoining: string;
    boardInterest: string;
  };
  paymentInfo: {
    paymentMethod: string;
    paymentDetails: {
      autoRenew: boolean;
      isAuthorized: boolean;
      consentToEmails: boolean;
      agreeToTerms: boolean;
      invoicePaymentMethod: string;
    };
  };
  otherInformation: {
    notes: string;
    chamberGroupInsurancePlanId: string;
    essoPlanId: string;
    customFields: Array<{
      id: string;
      label: string;
      value: string;
    }>;
  };
};

// Initial form state
const initialFormState: CreateMemberForm = {
  companyInfo: {
    membershipType: "",
    fullTimeEmployees: "",
    partTimeEmployees: "",
    companyName: "",
    companyDescription: "",
    companyWebsite: "",
    companyPhone: "",
    companyEmail: "",
    companyCategory: "",
    inBusinessSince: "",
  },
  contactInfo: {
    firstName: "",
    lastName: "",
    title: "",
    phone: "",
    mobilePhone: "",
    email: "",
  },
  address: {
    streetAddress: "",
    city: "",
    province: "",
    postalCode: "",
    country: "",
  },
  billingAddress: {
    streetAddress: "",
    city: "",
    province: "",
    postalCode: "",
    country: "",
  },
  socialMedia: {
    facebook: "",
    instagram: "",
    linkedin: "",
    tiktok: "",
    twitter: "",
    threads: "",
    youtube: "",
  },
  contactPersons: {
    primary: {
      firstName: "",
      lastName: "",
      title: "",
      companyPhone: "",
      mobilePhone: "",
      email: "",
    },
    billing: {
      firstName: "",
      lastName: "",
      title: "",
      companyPhone: "",
      mobilePhone: "",
      email: "",
    },
    perks: {
      firstName: "",
      lastName: "",
      title: "",
      companyPhone: "",
      mobilePhone: "",
      email: "",
    },
    hr: {
      firstName: "",
      lastName: "",
      title: "",
      companyPhone: "",
      mobilePhone: "",
      email: "",
    },
  },
  membershipDetails: {
    hearAboutSource: "",
    reasonsForJoining: "",
    boardInterest: "",
  },
  paymentInfo: {
    paymentMethod: "",
    paymentDetails: {
      autoRenew: false,
      isAuthorized: false,
      consentToEmails: false,
      agreeToTerms: false,
      invoicePaymentMethod: "",
    },
  },
  otherInformation: {
    notes: "",
    chamberGroupInsurancePlanId: "",
    essoPlanId: "",
    customFields: [],
  },
};

const CreateMember = () => {
  const [formData, setFormData] = useState<CreateMemberForm>(initialFormState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [useSameAsAddress, setUseSameAsAddress] = useState(false);

  // Company info handler
  const handleCompanyInfoChange = ({
    field,
    value,
  }: {
    field: keyof typeof formData.companyInfo;
    value: string;
  }) => {
    setFormData({
      ...formData,
      companyInfo: {
        ...formData.companyInfo,
        [field]: value,
      },
    });
  };

  // Contact info handler
  const handleContactInfoChange = ({
    field,
    value,
  }: {
    field: keyof typeof formData.contactInfo;
    value: string;
  }) => {
    setFormData({
      ...formData,
      contactInfo: {
        ...formData.contactInfo,
        [field]: value,
      },
    });
  };

  // Address handler
  const handleAddressChange = ({
    field,
    value,
  }: {
    field: keyof typeof formData.address;
    value: string;
  }) => {
    setFormData({
      ...formData,
      address: {
        ...formData.address,
        [field]: value,
      },
    });

    // If using same address for billing, update billing address too
    if (useSameAsAddress) {
      setFormData((prev) => ({
        ...prev,
        billingAddress: {
          ...prev.billingAddress,
          [field]: value,
        },
      }));
    }
  };

  // Billing address handler
  const handleBillingAddressChange = ({
    field,
    value,
  }: {
    field: keyof typeof formData.billingAddress;
    value: string;
  }) => {
    setFormData({
      ...formData,
      billingAddress: {
        ...formData.billingAddress,
        [field]: value,
      },
    });
  };

  // Social media handler
  const handleSocialMediaChange = ({
    platform,
    value,
  }: {
    platform: keyof typeof formData.socialMedia;
    value: string;
  }) => {
    setFormData({
      ...formData,
      socialMedia: {
        ...formData.socialMedia,
        [platform]: value,
      },
    });
  };

  // Contact persons handler
  const handleContactPersonChange = ({
    personType,
    field,
    value,
  }: {
    personType: keyof typeof formData.contactPersons;
    field: keyof typeof formData.contactPersons.primary;
    value: string;
  }) => {
    setFormData({
      ...formData,
      contactPersons: {
        ...formData.contactPersons,
        [personType]: {
          ...formData.contactPersons[personType],
          [field]: value,
        },
      },
    });
  };

  // Membership details handler
  const handleMembershipDetailsChange = ({
    field,
    value,
  }: {
    field: keyof typeof formData.membershipDetails;
    value: string;
  }) => {
    setFormData({
      ...formData,
      membershipDetails: {
        ...formData.membershipDetails,
        [field]: value,
      },
    });
  };

  // Payment info handler
  const handlePaymentInfoChange = ({
    field,
    value,
  }: {
    field: keyof typeof formData.paymentInfo;
    value: string;
  }) => {
    setFormData({
      ...formData,
      paymentInfo: {
        ...formData.paymentInfo,
        [field]: value,
      },
    });
  };

  // Payment details handler
  const handlePaymentDetailsChange = ({
    field,
    value,
  }: {
    field: keyof typeof formData.paymentInfo.paymentDetails;
    value: boolean | string;
  }) => {
    setFormData({
      ...formData,
      paymentInfo: {
        ...formData.paymentInfo,
        paymentDetails: {
          ...formData.paymentInfo.paymentDetails,
          [field]: value,
        },
      },
    });
  };

  // Other information handler
  const handleOtherInformationChange = ({
    field,
    value,
  }: {
    field: keyof Omit<typeof formData.otherInformation, "customFields">;
    value: string;
  }) => {
    setFormData({
      ...formData,
      otherInformation: {
        ...formData.otherInformation,
        [field]: value,
      },
    });
  };

  // Custom field handler
  const handleAddCustomField = () => {
    const newField = {
      id: `custom-${Date.now()}`,
      label: "",
      value: "",
    };

    setFormData({
      ...formData,
      otherInformation: {
        ...formData.otherInformation,
        customFields: [...formData.otherInformation.customFields, newField],
      },
    });
  };

  const handleUpdateCustomField = ({
    id,
    field,
    value,
  }: {
    id: string;
    field: "label" | "value";
    value: string;
  }) => {
    setFormData({
      ...formData,
      otherInformation: {
        ...formData.otherInformation,
        customFields: formData.otherInformation.customFields.map(
          (customField) =>
            customField.id === id
              ? { ...customField, [field]: value }
              : customField
        ),
      },
    });
  };

  const handleRemoveCustomField = ({ id }: { id: string }) => {
    setFormData({
      ...formData,
      otherInformation: {
        ...formData.otherInformation,
        customFields: formData.otherInformation.customFields.filter(
          (field) => field.id !== id
        ),
      },
    });
  };

  // Same as address handler
  const handleSameAsAddressChange = ({ checked }: { checked: boolean }) => {
    setUseSameAsAddress(checked);

    if (checked) {
      setFormData({
        ...formData,
        billingAddress: {
          ...formData.address,
        },
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Basic validation examples
    if (!formData.companyInfo.companyName.trim()) {
      newErrors.companyName = "Company name is required";
    }

    if (!formData.companyInfo.companyEmail.trim()) {
      newErrors.companyEmail = "Company email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.companyInfo.companyEmail)) {
      newErrors.companyEmail = "Invalid email format";
    }

    if (!formData.contactInfo.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.contactInfo.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.contactInfo.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formData.contactInfo.email)) {
      newErrors.email = "Invalid email format";
    }

    // Add more validation as needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Here you would normally send the data to your API
    console.log("Submitting form data:", formData);

    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      alert("Member created successfully!");
    }, 1500);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="max-w-4xl mx-auto">
        <CompanyInfoSection
          companyInfo={formData.companyInfo}
          onChange={handleCompanyInfoChange}
          errors={errors}
        />

        <ContactInfoSection
          contactInfo={formData.contactInfo}
          onChange={handleContactInfoChange}
          errors={errors}
        />

        <AddressSection
          address={formData.address}
          onChange={handleAddressChange}
          errors={errors}
        />

        <BillingAddressSection
          billingAddress={formData.billingAddress}
          onChange={handleBillingAddressChange}
          useSameAsAddress={useSameAsAddress}
          onSameAsAddressChange={handleSameAsAddressChange}
          errors={errors}
        />

        <SocialMediaSection
          socialMedia={formData.socialMedia}
          onChange={handleSocialMediaChange}
        />

        <ContactPersonsSection
          contactPersons={formData.contactPersons}
          onChange={handleContactPersonChange}
          errors={errors}
        />

        <MembershipDetailsSection
          membershipDetails={formData.membershipDetails}
          onChange={handleMembershipDetailsChange}
          errors={errors}
        />

        <PaymentInfoSection
          paymentInfo={formData.paymentInfo}
          onPaymentInfoChange={handlePaymentInfoChange}
          onPaymentDetailsChange={handlePaymentDetailsChange}
          errors={errors}
        />

        <OtherInformationSection
          otherInformation={formData.otherInformation}
          onOtherInformationChange={handleOtherInformationChange}
          onAddCustomField={handleAddCustomField}
          onUpdateCustomField={handleUpdateCustomField}
          onRemoveCustomField={handleRemoveCustomField}
          errors={errors}
        />

        <div className="mt-8 flex justify-center">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={isSubmitting}
            sx={{
              backgroundColor: "#E1A731",
              "&:hover": {
                backgroundColor: "#c78f1e",
              },
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              px: 4,
              py: 1.5,
            }}
          >
            {isSubmitting ? "Creating..." : "Create Member"}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default CreateMember;
